import type {
  Comment,
  EditableTrack,
  EditableTrackMetadata,
  Track,
} from 'types/track'
import {
  EDITED_TRACK_INIT,
  EDITED_TRACK_UPDATE,
  FORM_TRACK_MERGE_RESET,
  PLAYLIST_METADATA_SAVE,
  PLAYLIST_METADATA_SAVE_ERROR,
  PLAYLIST_METADATA_SAVE_SUCCESS,
  SWITCH_TERRITORY_WARNING,
  TRACK_ADD_WRITER_SUCCESS,
  TRACK_DELETE_WRITER_SUCCESS,
  TRACK_GROUP_CHANGE,
  TRACK_GROUP_CHANGE_ERROR,
  TRACK_GROUP_CHANGE_SUCCESS,
  TRACK_METADATA_COMMENT_CANCEL,
  TRACK_METADATA_COMMENT_DELETE,
  TRACK_METADATA_COMMENT_DELETE_ERROR,
  TRACK_METADATA_COMMENT_DELETE_SUCCESS,
  TRACK_METADATA_COMMENT_EDIT,
  TRACK_METADATA_COMMENT_EDIT_ERROR,
  TRACK_METADATA_COMMENT_EDIT_SUCCESS,
  TRACK_METADATA_COMMENT_SAVE,
  TRACK_METADATA_COMMENT_SAVE_ERROR,
  TRACK_METADATA_COMMENT_SAVE_SUCCESS,
  TRACK_METADATA_COMMENT_START_EDIT,
  TRACK_METADATA_FETCH_SUCCESS,
  TRACK_METADATA_SAVE,
  TRACK_METADATA_SAVE_ERROR,
  TRACK_METADATA_SAVE_SUCCESS,
  TRACK_UPDATE,
  TRACK_UPDATE_ERROR,
  TRACK_UPDATE_SUCCESS,
  TRACK_UPDATE_WRITER_SUCCESS,
} from '@disco/constants/ActionTypes'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'
import { getCommonGroups } from '@disco/utils/groups'
import { updateItem } from '@disco/utils/immutable'
const editedTrackInitialState: EditableTrack = {
  id: null,
  url: undefined,
  metadata: {
    // null = user explicitly cleared/reset; undefined = untouched initial state
    Artwork: {
      mixed: false,
      value: undefined,
    },
    Title: {
      mixed: false,
      value: '',
    },
    Album: {
      mixed: false,
      value: '',
    },
    Artist: {
      mixed: false,
      value: '',
    },
    Composer: {
      mixed: false,
      value: '',
    },
    Grouping: {
      mixed: false,
      value: '',
    },
    Genre: {
      mixed: false,
      value: '',
    },
    Year: {
      mixed: false,
      value: '',
    },
    Lyrics: {
      mixed: false,
      value: '',
    },
    Comment: {
      mixed: false,
      value: '',
    },
    TrackNumber: {
      mixed: false,
      value: '',
    },
    Tracks: {
      mixed: false,
      value: '',
    },
    BPM: {
      mixed: false,
      value: '',
    },
    ProNumber: {
      mixed: false,
      value: '',
    },
    ISRC: {
      mixed: false,
      value: '',
    },
    'External Id': {
      mixed: false,
      value: undefined,
    },
  },
  comments: [],
  track_writers: [],
  groups: [],
}
export type ServerState = {
  saving: boolean
  saved: boolean
  showError: boolean | string
  error: string | null | undefined
  showTerritoryWarning: boolean
  host: string
  editedTrack: EditableTrack
  originalEditedTrackMetadata: EditableTrackMetadata
}
const initialState: ServerState = {
  saving: false,
  saved: true,
  showError: false,
  error: null,
  showTerritoryWarning: false,
  host: typeof window !== 'undefined' ? (window.backend ?? '') : '',
  editedTrack: editedTrackInitialState,
  originalEditedTrackMetadata: {},
}

function dropEditingComments(
  comments: EditableTrack['comments'],
  originalTrack: Track,
): Partial<Comment>[] {
  return comments
    .filter(comment => !comment.isNew)
    .map(comment => {
      if (comment.isEditing) {
        return (originalTrack.comments || []).find(
          origC => origC.id === comment.id,
        )
      } else {
        return comment
      }
    })
    .filter((c): c is Partial<Comment> => c != null)
}

export function serverReducer(
  state: ServerState = initialState,
  action: Record<string, any> = {},
): ServerState {
  switch (action.type) {
    case TRACK_METADATA_FETCH_SUCCESS: {
      const { fetchingTracks, tracksMetadata, tracksComments, trackWriters } =
        action
      let updateFields

      if (action.fetchingTracks) {
        // merge all info for playlist
        updateFields = {
          ...getCommonBasicMetadataFields(fetchingTracks),
          ...getCommonExtraMetadataFields(tracksMetadata),
        }
      } else {
        updateFields = {}
      }

      // TODO: join similar tracksComments so people can delete comments in bulk with Bulk Metadata
      // Placeholder:
      const displayingComments = tracksComments
        ? tracksComments[0]
        : state.editedTrack.comments
      return {
        ...state,

        originalEditedTrackMetadata: {
          ...state.originalEditedTrackMetadata,
          ...updateFields,
        },
        editedTrack: {
          ...state.editedTrack,
          // update comments to fix blank commentId for comments added in bulk
          comments: displayingComments,

          metadata: { ...state.editedTrack.metadata, ...updateFields },
          track_writers: trackWriters
            ? trackWriters[0]
            : state.editedTrack.track_writers,
          groups: fetchingTracks
            ? getCommonGroups(fetchingTracks)
            : state.editedTrack.groups,
        },
      }
    }

    case EDITED_TRACK_INIT:
      return {
        ...state,
        saved: false,
        showError: false,
        originalEditedTrackMetadata: action.editedTrack.metadata,
        editedTrack: action.editedTrack,
      }

    case EDITED_TRACK_UPDATE:
      return { ...state, saved: false, editedTrack: action.editedTrack }

    case TRACK_UPDATE:
    case TRACK_METADATA_SAVE:
    case PLAYLIST_METADATA_SAVE:
    case TRACK_GROUP_CHANGE:
      return { ...state, saving: true }

    case TRACK_UPDATE_SUCCESS:
    case TRACK_METADATA_SAVE_SUCCESS:
      return {
        ...state,
        originalEditedTrackMetadata: state.editedTrack.metadata,
        showError: false,
        saving: false,
        saved: true,
      }

    case TRACK_GROUP_CHANGE_SUCCESS:
      return {
        ...state,
        editedTrack: { ...state.editedTrack, groups: action.commonGroups },
        showError: false,
        saving: false,
        saved: true,
      }

    case TRACK_UPDATE_ERROR:
    case TRACK_METADATA_SAVE_ERROR:
      return {
        ...state,
        saving: false,
        saved: false,
        showError: 'save_metadata',
      }

    case PLAYLIST_METADATA_SAVE_SUCCESS:
      return { ...state, saving: false, saved: true }

    case PLAYLIST_METADATA_SAVE_ERROR:
    case TRACK_GROUP_CHANGE_ERROR:
      return { ...state, saving: false, saved: false }

    case TRACK_METADATA_COMMENT_SAVE:
    case TRACK_METADATA_COMMENT_EDIT:
    case TRACK_METADATA_COMMENT_DELETE:
      return {
        ...state,
        saving: true,
        saved: false,
        showError: false,
        error: null,
      }

    case TRACK_METADATA_COMMENT_SAVE_SUCCESS: {
      const { index, comment } = action
      const updatedComments = state.editedTrack.comments.slice()
      updatedComments.splice(index, 1)
      updatedComments.unshift(comment)
      return {
        ...state,
        saving: false,
        saved: true,
        showError: false,
        error: null,
        editedTrack: { ...state.editedTrack, comments: updatedComments },
      }
    }

    case TRACK_DELETE_WRITER_SUCCESS: {
      const { trackId, splitId } = action
      if (state.editedTrack.id !== trackId) return state
      return {
        ...state,
        editedTrack: {
          ...state.editedTrack,
          track_writers:
            state.editedTrack.track_writers?.filter(s => s.id !== splitId) ||
            [],
        },
      }
    }

    case TRACK_ADD_WRITER_SUCCESS: {
      const { track, writer } = action
      if (state.editedTrack.id !== track && state.editedTrack.id) return state
      return {
        ...state,
        editedTrack: {
          ...state.editedTrack,
          track_writers: [...(state.editedTrack.track_writers || []), writer],
        },
      }
    }

    case TRACK_UPDATE_WRITER_SUCCESS: {
      const { track, split } = action
      if (state.editedTrack.id !== track) return state
      const { track_writers } = state.editedTrack
      let updatedTrackWriters = track_writers

      if (track_writers) {
        const splitIndex: number = track_writers.findIndex(
          w => w.id === split.id,
        )
        updatedTrackWriters = updateItem(track_writers, splitIndex, split)
      }

      return {
        ...state,
        editedTrack: {
          ...state.editedTrack,
          track_writers: updatedTrackWriters,
        },
      }
    }

    case TRACK_METADATA_COMMENT_SAVE_ERROR:
    case TRACK_METADATA_COMMENT_EDIT_ERROR:
      return {
        ...state,
        saving: false,
        saved: true,
        showError: 'save_comment',
        error: action.error,
      }

    case TRACK_METADATA_COMMENT_START_EDIT: {
      const { index, originalTrack } = action
      const updatedComment = {
        ...state.editedTrack.comments[index],
        isEditing: true,
      }
      const comments = state.editedTrack.comments
      return {
        ...state,
        editedTrack: {
          ...state.editedTrack,
          comments: [
            ...dropEditingComments(comments.slice(0, index), originalTrack),
            updatedComment,
            ...dropEditingComments(comments.slice(index + 1), originalTrack),
          ],
        },
      }
    }

    case TRACK_METADATA_COMMENT_EDIT_SUCCESS: {
      const { index, comment } = action
      return {
        ...state,
        saving: false,
        saved: true,
        showError: false,
        error: null,
        editedTrack: {
          ...state.editedTrack,
          comments: updateItem(state.editedTrack.comments, index, comment),
        },
      }
    }

    case TRACK_METADATA_COMMENT_DELETE_SUCCESS: {
      const { index } = action
      return {
        ...state,
        saving: false,
        saved: true,
        showError: false,
        error: null,
        editedTrack: {
          ...state.editedTrack,
          comments: [
            ...state.editedTrack.comments.slice(0, index),
            ...state.editedTrack.comments.slice(index + 1),
          ],
        },
      }
    }

    case TRACK_METADATA_COMMENT_DELETE_ERROR:
      return {
        ...state,
        saving: false,
        saved: true,
        showError: 'delete_comment',
        error: action.error,
      }

    case TRACK_METADATA_COMMENT_CANCEL: {
      const { originalTrack } = action
      return {
        ...state,
        editedTrack: {
          ...state.editedTrack,
          comments: dropEditingComments(
            state.editedTrack.comments,
            originalTrack,
          ),
        },
      }
    }

    case FORM_TRACK_MERGE_RESET:
      return { ...state, editedTrack: editedTrackInitialState }

    case SWITCH_TERRITORY_WARNING:
      return { ...state, showTerritoryWarning: action.status }

    default:
      return state
  }
}
